import { useState } from 'react'
import { Input, Button } from '../ui'
import { GoogleButton } from './GoogleButton'
import { useTranslation } from '../../contexts/LanguageContext'

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>
  onGoogleSignIn: () => Promise<void>
  onForgotPassword: () => void
  onSwitchToRegister: () => void
  error?: string | null
  loading?: boolean
  hideHeader?: boolean
}

export function LoginForm({
  onSubmit,
  onGoogleSignIn,
  onForgotPassword,
  onSwitchToRegister,
  error,
  loading,
  hideHeader,
}: LoginFormProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    if (!email.trim()) {
      setLocalError(t.auth.emailRequired)
      return
    }

    if (!password) {
      setLocalError(t.auth.passwordRequired)
      return
    }

    try {
      await onSubmit(email, password)
    } catch {
      // Error handled by parent
    }
  }

  const displayError = error || localError

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-gray-900">
            {t.auth.welcomeBack}
          </h2>
          <p className="mt-2 text-gray-600">
            {t.auth.loginSubtitle}
          </p>
        </div>
      )}

      <GoogleButton onClick={onGoogleSignIn} loading={loading} />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">{t.auth.orContinueEmail}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {displayError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{displayError}</p>
          </div>
        )}

        <Input
          label={t.auth.email}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.auth.emailPlaceholder}
          autoComplete="email"
        />

        <div>
          <Input
            label={t.auth.password}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.auth.passwordPlaceholder}
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={onForgotPassword}
            className="mt-1.5 text-sm text-primary hover:text-primary-hover transition-colors"
          >
            {t.auth.forgotPassword}
          </button>
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          isLoading={loading}
        >
          {t.auth.login}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600">
        {t.auth.noAccount}{' '}
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="font-medium text-primary hover:text-primary-hover transition-colors"
        >
          {t.auth.signUpFree}
        </button>
      </p>
    </div>
  )
}
